import { inject, Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SERVICES_API, WwwAppState, WwwRouteSlug } from '@pro-connect/shared';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { jsonToHtml } from 'shared/src/utils/helpers/xss';
import { ProExperienceEvnetService } from './pro-experience-event.service';
import { IProExperienceEvent } from './models/pro-experience-event';
import { Navigate } from '@ngxs/router-plugin';

export class ProExperienceEventStateGetEventDetails {
  public static readonly type = '[ProExperienceEventState] get event';

  constructor(public payload: { slug: string }) {}
}

export interface ProExperienceEventStateModel {
  loading: boolean;
  event: IProExperienceEvent | null;
}

@State<ProExperienceEventStateModel>({
  name: 'proExperienceEvent',
  defaults: {
    loading: false,
    event: null,
  },
})
@Injectable()
export class ProExperienceEventState {
  private readonly _store = inject(Store);
  public constructor(
    private readonly _proExperienceEventService: ProExperienceEvnetService,
    @Inject(SERVICES_API)
    private readonly _servicesApi: string,
  ) {}

  @Selector()
  public static loading({ loading }: ProExperienceEventStateModel): boolean {
    return loading;
  }

  @Selector()
  public static event({ event }: ProExperienceEventStateModel): IProExperienceEvent | null {
    return event;
  }

  @Action(ProExperienceEventStateGetEventDetails)
  public getEvent(
    state: StateContext<ProExperienceEventStateModel>,
    { payload: { slug } }: ProExperienceEventStateGetEventDetails,
  ): Observable<any> {
    const { patchState, dispatch } = state;

    patchState({
      loading: true,
    });

    return this._proExperienceEventService.getEventDetails(slug).pipe(
      tap((event: IProExperienceEvent) => {
        patchState({
          event: {
            ...event,
            description: event.description
              ? jsonToHtml(event.description).replace(new RegExp('/uploads/', 'g'), `${this._servicesApi}/uploads/`)
              : null,
            information: event.information
              ? jsonToHtml(event.information).replace(new RegExp('/uploads/', 'g'), `${this._servicesApi}/uploads/`)
              : null,
            price: Math.round(event.price),
            events: [...event.events],
          },
        });
      }),
      catchError((err) => {
        const user = this._store.selectSignal(WwwAppState.user);
        dispatch(new Navigate([`/${user() ? WwwRouteSlug.ACCOUNT : ''}`, WwwRouteSlug.PRO_EXPERIENCE]));
        return throwError(() => err);
      }),
      finalize(() => {
        patchState({
          loading: false,
        });
      }),
    );
  }
}
